import { Link, useNavigate } from 'react-router-dom';
import { Button, Input, Switch } from 'antd';
import { useEffect, useState } from 'react';

import { HomeOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { usePriceSimulatorV3Context } from 'contexts/PriceSimulatorV3Context';
import { colorConstants } from 'config/colorConstants';
import ProjectScenarioExpandableTable, {
    type ProjectRow,
    type ScenarioRow,
} from 'components/molecules/PricingSimulatorV3/ProjectScenarioExpandableTable';
import PriceSimulatorCreateEditModal from 'components/molecules/PPA/Modals/PriceSimulatorCreateEditModal';
import Loader from 'components/molecules/Loader';
import { PPA_HOME_ROUTE } from 'config/urls';
import { PpaProjectActionType, PpaTableRowAction, tableRowActionToProjectAction } from 'types/ppa';

const Breadcrumb = ({ title }: { title: string }) => (
    <nav className="flex items-center" aria-label="Breadcrumb">
        <Link to="/" className="flex items-center text-black/50" aria-label="Home">
            <HomeOutlined className="text-[12px]" />
        </Link>
        <span className="px-2 text-[10px] leading-[22px]" style={{ color: colorConstants.breadcrumbSlash }} aria-hidden>
            /
        </span>
        <span className="text-[12px] font-medium leading-[22px] text-fontColor">{title}</span>
    </nav>
);

const PageHeaderSection = ({ title, onBack }: { title: string; onBack: () => void }) => (
    <div className="flex justify-between items-center w-full pb-2">
        <div className="flex items-center gap-2">
            <button
                type="button"
                onClick={onBack}
                className="flex h-8 w-8 items-center justify-center rounded hover:bg-backgroundGreyAlt transition-colors"
                aria-label="Go back"
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M10.7071 5.29289C11.0976 5.68342 11.0976 6.31658 10.7071 6.70711L6.41421 11H20C20.5523 11 21 11.4477 21 12C21 12.5523 20.5523 13 20 13H6.41421L10.7071 17.2929C11.0976 17.6834 11.0976 18.3166 10.7071 18.7071C10.3166 19.0976 9.68342 19.0976 9.29289 18.7071L3.29289 12.7071C2.90237 12.3166 2.90237 11.6834 3.29289 11.2929L9.29289 5.29289C9.68342 4.90237 10.3166 4.90237 10.7071 5.29289Z"
                        fill="#161227"
                    />
                </svg>
            </button>
            <h1 className="m-0 text-2xl font-semibold leading-8 tracking-[0.5px] text-fontColor">{title}</h1>
        </div>
    </div>
);

const getTabClassName = (isActive: boolean) =>
    `h-6 min-w-[145px] rounded-lg px-4 text-[14px] font-medium leading-[22px] ${
        isActive
            ? 'border border-purpleFont bg-white text-purpleFont shadow-[0px_1px_2px_rgba(0,0,0,0.03),0px_1px_6px_rgba(0,0,0,0.02),0px_2px_4px_rgba(0,0,0,0.02)]'
            : ''
    }`;

type ScenarioManagerFeature = {
    feature_id?: string;
    feature_name?: string;
    feature_type?: string;
    options?: {
        columns?: Array<{
            id_type?: string;
            id?: string;
            label?: string;
            is_default?: boolean;
            is_sort?: boolean;
            is_filter?: boolean;
            options?: {
                id_type?: string;
                id?: string;
                items?: string[];
            };
        }>;
    };
};

const PriceSimulatorV3 = () => {
    const handleBack = () => navigate(PPA_HOME_ROUTE);
    const navigate = useNavigate();
    const {
        scenarioManagerMeta,
        isScenarioManagerMetaLoading,
        isScenarioManagerMetaPending,
        myScenarioOnly,
        setMyScenarioOnly,
        projectSearch,
        setProjectSearch,
    } = usePriceSimulatorV3Context();
    const title = scenarioManagerMeta?.title ?? '';
    const subtitle = scenarioManagerMeta?.subtitle ?? '';
    const features = (scenarioManagerMeta?.features ?? []) as ScenarioManagerFeature[];
    const tabs = features.filter((feature) => feature.feature_type === 'table');
    const primaryAction = features.find(
        (feature) => feature.feature_id === 'create_project' || feature.feature_type === 'form',
    );
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const [createProjectModalOpen, setCreateProjectModalOpen] = useState(false);
    const [editProjectModalOpen, setEditProjectModalOpen] = useState(false);
    const [editProjectParams, setEditProjectParams] = useState<Record<string, unknown>>({});
    const [editProjectActionType, setEditProjectActionType] = useState(PpaProjectActionType.Edit);
    const [searchInput, setSearchInput] = useState(projectSearch);
    const activeTableColumns = tabs[activeTabIndex]?.options?.columns ?? [];

    const handleTableAction = (action: PpaTableRowAction, record: ProjectRow | ScenarioRow) => {
        if (!('project_id' in record)) return;
        const project = record as ProjectRow;
        const projectAction = tableRowActionToProjectAction(action);
        if (!projectAction) {
            return;
        }

        if (projectAction === PpaProjectActionType.Edit) {
            setEditProjectActionType(PpaProjectActionType.Edit);
            setEditProjectParams({
                project_id: project.project_id,
                project_name: project.project_name,
                is_private: project.is_private,
                simulation_time_period: project.simulation_period,
                project_filters: project.scope ?? [],
            });
            setEditProjectModalOpen(true);
            return;
        }

        if (projectAction === PpaProjectActionType.Duplicate) {
            setEditProjectActionType(PpaProjectActionType.Duplicate);
            setEditProjectParams({
                project_name: project.project_name,
                is_private: project.is_private,
                simulation_time_period: project.simulation_period,
                project_filters: project.scope ?? [],
            });
            setEditProjectModalOpen(true);
        }
    };

    const handleScenarioClick = (record: { project_id: number; scenario_id: number; scenario_name?: string }) => {
        navigate(`/ppa/price-simulator-v3/project/${record.project_id}?scenarioId=${record.scenario_id}`, {
            state: {
                projectId: record.project_id,
                scenarioId: record.scenario_id,
                scenarioName: record.scenario_name,
            },
        });
    };

    useEffect(() => {
        setActiveTabIndex((currentValue) => (currentValue < tabs.length ? currentValue : 0));
    }, [tabs.length]);

    useEffect(() => {
        setSearchInput(projectSearch);
    }, [projectSearch]);

    useEffect(() => {
        const debounceTimeout = window.setTimeout(() => {
            if (projectSearch !== searchInput) {
                setProjectSearch(searchInput);
            }
        }, 800);

        return () => window.clearTimeout(debounceTimeout);
    }, [projectSearch, searchInput, setProjectSearch]);

    if (isScenarioManagerMetaLoading || isScenarioManagerMetaPending) {
        return (
            <div className="flex w-full bg-white" style={{ minHeight: 'calc(100vh - 100px)' }}>
                <Loader size="default" />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2 py-2">
            <Breadcrumb title={title} />
            <div className="rounded-xl bg-white p-4">
                <PageHeaderSection title={title} onBack={handleBack} />
                {subtitle ? <p className="m-0 text-[14px] leading-[22px] text-fontColor/70">{subtitle}</p> : null}
                <div className="mt-2 flex flex-row items-center justify-between gap-4 h-full">
                    <div className="flex rounded-lg p-1" style={{ backgroundColor: colorConstants.tabBackground }}>
                        {tabs.map((feature, index) => {
                            const isActive = activeTabIndex === index;
                            const label = feature.feature_name ?? feature.feature_id ?? 'Feature';
                            const key = feature.feature_id ?? feature.feature_name ?? `feature-${index}`;

                            return (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => setActiveTabIndex(index)}
                                    className={getTabClassName(isActive)}
                                    style={isActive ? undefined : { color: colorConstants.inactiveTabText }}
                                >
                                    {label}
                                </button>
                            );
                        })}
                    </div>

                    <div className="flex flex-row items-center gap-4">
                        <div className="flex flex-row items-center gap-2">
                        <span className="m-0 text-[14px] text-blackTitle whitespace-nowrap font-medium">My Scenarios</span>
                            <Switch
                                checked={myScenarioOnly}
                                onChange={setMyScenarioOnly}
                                aria-label="Show only projects I worked on"
                            />
                        </div>
                        <Input
                            allowClear
                            value={searchInput}
                            onChange={(event) => setSearchInput(event.target.value)}
                            placeholder="Search projects, scenarios etc"
                            suffix={<SearchOutlined className="text-black/70" />}
                            className="w-[340px] !rounded-lg !py-1 !border-black/25 !shadow-none"
                        />
                        {primaryAction ? (
                            <Button
                                type="primary"
                                className="!h-8 !rounded-md !border-purpleFont !bg-purpleFont !px-[15px] text-[14px] !font-semibold !flex !items-center !justify-center"
                                icon={<PlusOutlined />}
                                onClick={() => setCreateProjectModalOpen(true)}
                            >
                                {primaryAction.feature_name ?? 'Create'}
                            </Button>
                        ) : null}
                    </div>
                </div>
                {activeTableColumns.length ? (
                    <ProjectScenarioExpandableTable
                        columnsConfig={activeTableColumns}
                        onScenarioClick={handleScenarioClick}
                        onAction={handleTableAction}
                    />
                ) : null}
            </div>
            <PriceSimulatorCreateEditModal
                open={createProjectModalOpen}
                projectActionType={PpaProjectActionType.Create}
                onCancel={() => setCreateProjectModalOpen(false)}
                onSubmitFinish={() => setCreateProjectModalOpen(false)}
            />
            <PriceSimulatorCreateEditModal
                open={editProjectModalOpen}
                projectActionType={editProjectActionType}
                editParams={editProjectParams}
                onCancel={() => setEditProjectModalOpen(false)}
                onSubmitFinish={() => setEditProjectModalOpen(false)}
            />
        </div>
    );
};

export default PriceSimulatorV3;
